import React, { FC, InputHTMLAttributes, useEffect, useState, useRef } from 'react';
import { FaChevronDown } from 'react-icons/fa';

import { TDropdownOption } from '~types/components/labelledDropdown';

import Text from '../Text';

import UtilFunctions from '~utils/functions';
import * as DropdownStyles from './_labelledDropdown.module.scss';
import CustomClipLoader from '../CustomClipLoader';

type LabelledDropdownProps = InputHTMLAttributes<HTMLInputElement> & {
    label: string;
    options: TDropdownOption[];
    optionSelected: boolean;
    noOptionsMessage?: string;
    isRow?: boolean;
    isFilter?: boolean;
    error?: string;
    className?: string;
    onSelectOption?: (option: TDropdownOption) => void;
    disabled?: boolean;
    isLoading?: boolean;

    // for input
    value?: string;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// Create a static variable to track the currently open dropdown
let activeDropdownRef: HTMLDivElement | null = null;

const LabelledDropdown: FC<LabelledDropdownProps> = ({
    label,
    options,
    noOptionsMessage,
    isRow,
    isFilter,
    error,
    className,
    onSelectOption,
    optionSelected,
    disabled,
    isLoading,

    // for input
    value,
    onChange,
    ...otherProps
}) => {
    const [showOptions, setShowOptions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef<(HTMLDivElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    // Define a safe regex creation function
    const createSafeRegex = (valueString: string | null): RegExp => {
        if (!valueString) {
            return new RegExp('', 'i'); // Match everything case-insensitively.
        }

        // Sanitize input (allow only alphanumeric characters and spaces and few special character)
        const sanitizedValue = valueString.replace(/[^a-zA-Z0-9-',./@ ]/g, '');

        // Return the case-insensitive regex
        return new RegExp(sanitizedValue, 'i');
    };

    const [safeRegex, setSafeRegex] = useState(createSafeRegex(value ? value : null));

    // Filtering logic for options
    const filteredOptions = options.filter(option =>
        safeRegex !== null && safeRegex.test(option.label)
    );

    useEffect(() => {
        setSafeRegex(createSafeRegex(value ? value : null));
    }, [value]);

    useEffect(() => {
        // Global click handler
        const handleGlobalClick = (event: MouseEvent) => {
            if (!(event.target instanceof HTMLElement)) return;

            // If clicking inside current dropdown container, do nothing
            if (containerRef.current?.contains(event.target)) {
                return;
            }

            // If clicking outside and this dropdown is open, close it
            if (dropdownRef.current === activeDropdownRef) {
                setShowOptions(false);
                activeDropdownRef = null;
            }
        };

        document.addEventListener('mousedown', handleGlobalClick);
        return () => {
            document.removeEventListener('mousedown', handleGlobalClick);
        };
    }, []);

    useEffect(() => {
        if (showOptions) {
            document.addEventListener('keydown', handleKeyDown);
        } else {
            document.removeEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showOptions, highlightedIndex]);

    useEffect(() => {
        optionsRef.current = optionsRef.current.slice(0, filteredOptions.length);
    }, [filteredOptions]);

    // Listen for close events
    useEffect(() => {
        const handleClose = () => {
            setShowOptions(false);
            setHighlightedIndex(-1);
        };

        containerRef.current?.addEventListener('closeDropdown', handleClose);

        return () => {
            containerRef.current?.removeEventListener('closeDropdown', handleClose);
        };
    }, []);

    const chevronStyles = UtilFunctions.Styles.concatClassNames([
        DropdownStyles['dropdown-icon'],
        (showOptions ? DropdownStyles['is-active'] : ''),
        disabled ? DropdownStyles['is-disabled'] : '',
    ]);

    const scrollOptionIntoView = (index: number) => {
        const option = optionsRef.current[index];
        if (option) {
            option.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth',
            });
        }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightedIndex((prevIndex) => {
                let newIndex = 0;
                if (optionSelected) {
                    newIndex = prevIndex < options.length - 1 ? prevIndex + 1 : prevIndex;
                } else {
                    newIndex = prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : prevIndex;
                }
                scrollOptionIntoView(newIndex);
                return newIndex;
            });
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex((prevIndex) => {
                const newIndex = prevIndex > 0 ? prevIndex - 1 : prevIndex;
                scrollOptionIntoView(newIndex);
                return newIndex;
            });
        } else if (event.key === 'Enter' && highlightedIndex !== -1) {
            if (optionSelected) {
                handleSelectedOption(options[highlightedIndex]);
            } else {
                handleSelectedOption(filteredOptions[highlightedIndex]);
            }
        }
    };

    const handleSelectedOption = (option: TDropdownOption) => {
        setShowOptions(false);
        setHighlightedIndex(-1);
        activeDropdownRef = null;
        if (onSelectOption) onSelectOption(option);
        if (onChange) onChange({ target: { value: option.label } } as React.ChangeEvent<HTMLInputElement>);
    };

    const renderOptions = () => {
        if (isLoading) {
            return (
                <div className={DropdownStyles['dropdown-option']}>
                    <CustomClipLoader blackBorder />
                </div>
            );
        }

        if (!options || options.length < 1 || !filteredOptions || filteredOptions.length < 1) {
            return (
                <div className={DropdownStyles['dropdown-option-disabled']}>
                    {noOptionsMessage || 'No options found'}
                </div>
            );
        }

        if (optionSelected) {
            return options.map((option, index) => {
                return (
                    <div
                        ref={el => optionsRef.current[index] = el}
                        onClick={() => handleSelectedOption(option)}
                        className={`${DropdownStyles['dropdown-option']}${index === highlightedIndex ? ` ${DropdownStyles['is-highlighted']}` : ''}`}
                        key={index}
                    >
                        {option.label}
                    </div>
                );
            });
        }

        return filteredOptions.map((option, index) => {
            return (
                <div
                    ref={el => optionsRef.current[index] = el}
                    onClick={() => handleSelectedOption(option)}
                    className={`${DropdownStyles['dropdown-option']}${index === highlightedIndex ? ` ${DropdownStyles['is-highlighted']}` : ''}`}
                    key={index}
                >
                    <p>{option.label}</p>
                </div>
            );
        });
    };

    const inputWrapperStyle = UtilFunctions.Styles.concatClassNames([
        DropdownStyles['input-container'],
        (isFilter ? DropdownStyles['is-filter'] : ''),
        (showOptions ? DropdownStyles['is-active'] : ''),
        (disabled ? DropdownStyles['is-disabled'] : ''),
    ]);

    const dropdownStyles = UtilFunctions.Styles.concatClassNames([
        DropdownStyles.dropdown,
        (isRow ? DropdownStyles['is-row'] : ''),
        (showOptions ? '' : DropdownStyles['is-hidden']),
        label ? '' : DropdownStyles['no-label'],
    ]);

    const toggleDropdown = () => {
        if (disabled) return;

        if (!showOptions) {
            // Close the currently open dropdown if it exists and is different from this one
            if (activeDropdownRef && activeDropdownRef !== dropdownRef.current) {
                // Find and close the active dropdown
                const activeDropdownComponent = Array.from(document.querySelectorAll(`.${DropdownStyles.container}`))
                    .find(container => container.contains(activeDropdownRef));
                if (activeDropdownComponent) {
                    const event = new CustomEvent('closeDropdown');
                    activeDropdownComponent.dispatchEvent(event);
                }
            }
            // Set this as the active dropdown
            activeDropdownRef = dropdownRef.current;
        } else {
            // If closing, clear the active dropdown
            activeDropdownRef = null;
        }

        setShowOptions(!showOptions);
        setHighlightedIndex(-1);
    };

    return (
        <div
            ref={containerRef}
            className={isRow ? `${DropdownStyles['row-container']} ${className}` : `${DropdownStyles.container} ${className}`}
        >
            <Text
                label={label}
                isBold
                className={DropdownStyles.label}
            />

            <div className={DropdownStyles['display-column']}>
                <div className={inputWrapperStyle}>
                    <input
                        type='text'
                        value={value}
                        onClick={toggleDropdown}
                        onChange={onChange}
                        className={DropdownStyles.input}
                        disabled={disabled}
                        {...otherProps}
                    />

                    <FaChevronDown
                        size={20}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleDropdown();
                        }}
                        className={chevronStyles}
                    />
                </div>

                {error && (
                    <Text
                        label={error}
                        isError
                        className={DropdownStyles.error}
                    />
                )}

                <div
                    ref={dropdownRef}
                    className={dropdownStyles}
                >
                    {renderOptions()}
                </div>
            </div>

        </div>
    );
};

export default LabelledDropdown;
